import { memo, useMemo, useState } from "react";
import { Button, Col, Modal, Row, Tag } from "antd";
import { DownSquareOutlined, PlusSquareOutlined, UpSquareOutlined } from "@ant-design/icons";
import { useDeepCompareEffect } from "ahooks";
import update from 'immutability-helper';
import { useWxworkUsers } from "@/hooks";
import { UserAddressBookList } from "./UserAddressBookList";

type Props = {
  id?: string;
  value?: number[];
  max?: number;
  onChange?: (value: number[]) => void;
}

export const UserAddressBookSelector: React.FC<Props> = memo((props) => {
  const { id, value = [], max, onChange } = props;
  const [listOpen, setListOpen] = useState(false);
  const users = useWxworkUsers();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(value || []);
  const [expanded, setExpanded] = useState(false);

  const selectedUsers = useMemo(() => {
    if (!users || !value) return [];
    return users.filter(user => value.includes(user.cybrosUserID));
  }, [users, value]);

  const showSelectedUsers = useMemo(() => {
    if (selectedUsers.length > 12 && !expanded) return selectedUsers.slice(0, 12);
    return selectedUsers;
  }, [expanded, selectedUsers]);

  useDeepCompareEffect(() => {
    setSelectedUserIds(value || []);
  }, [value]);

  const handleOk = () => {
    setListOpen(false);
    if (onChange) onChange(selectedUserIds);
  };

  const handleCancel = () => {
    setListOpen(false);
  };

  const handleChange = (userIds: number[]) => {
    setSelectedUserIds(userIds);
  };

  const handleClose = (userId: number) => {
    const index = selectedUserIds.findIndex(item => item === userId);
    const userIds = update(selectedUserIds, {
      $splice: [[index, 1]],
    });
    if (onChange) onChange(userIds);
  };

  return (
    <Row id={id} gutter={[8, 8]}>
      {showSelectedUsers.map(item => (
        <Col key={item.cybrosUserID} span={12} className="flex items-center">
          <Tag
            className="w-full h-8 flex justify-between items-center mr-0"
            closable
            onClose={() => handleClose(item.cybrosUserID)}
          >
            {item.name}
          </Tag>
        </Col>
      ))}
      {selectedUsers.length > 12 && (
        <Col span={12} className="flex items-center">
          <Button
            block
            icon={expanded ? ( <UpSquareOutlined /> ) : ( <DownSquareOutlined /> )}
            className="text-left px-[7px]"
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? '收起' : `共${selectedUsers.length}人`}
          </Button>
        </Col>
      )}
      <Col flex={1}>
        <Button
          block
          icon={( <PlusSquareOutlined /> )}
          className="text-left px-[7px]"
          onClick={() => setListOpen(true)}
        >
          添加人员
        </Button>
      </Col>
      <Modal
        open={listOpen}
        width={700}
        closable={false}
        styles={{
          body: { padding: 0 },
        }}
        okButtonProps={{
          disabled: selectedUserIds.length === 0,
        }}
        destroyOnHidden
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <UserAddressBookList
          max={max}
          value={selectedUserIds}
          onChange={handleChange}
        />
      </Modal>
    </Row>
  );
});
